
export interface AIProjectCreator {
  name: string;
  avatar: string;
  verified: boolean;
  followers: string;
  bio: string;
}

export interface AIProjectComment {
  id: number;
  user: string;
  avatar: string;
  comment: string;
  date: string;
  likes: number;
  replies: AIProjectComment[];
}

export interface AIProjectReview {
  id: number;
  user: string;
  avatar: string;
  rating: number;
  title: string;
  review: string;
  date: string;
  helpful: number;
  categories: {
    visuals: number;
    story: number;
    audio: number;
    creativity: number;
  };
}

export interface AIProject {
  id: number;
  title: string;
  slug: string;
  creator: AIProjectCreator;
  thumbnail: string;
  videoUrl: string;
  duration: string;
  views: string;
  likes: string;
  comments: number;
  category: string;
  description: string;
  longDescription: string;
  tools: string[];
  featured: boolean;
  datePublished: string;
  quality: '4K' | 'HD' | 'SD';
  style: string;
  awards: string[];
  behindTheScenes: string;
  soundtrack: string;
  resolution: string;
  fps: string;
  aspectRatio: string;
  tags: string[];
  averageRating?: number;
  totalReviews?: number;
  ratingBreakdown?: {
    visuals: number;
    story: number;
    audio: number;
    creativity: number;
  };
}

export const aiProjects: AIProject[] = [
  {
    id: 1,
    title: 'The Last Algorithm',
    slug: 'sci-fi-the-last-algorithm',
    creator: {
      name: 'Alex Chen',
      avatar: 'https://public.readdy.ai/ai/img_res/402a74002b1876fa4cc9da65d22786d7.jpg',
      verified: true,
      followers: '45.2K',
      bio: 'AI filmmaker and creative technologist. Pushing the boundaries of AI-generated cinema since 2023.'
    },
    thumbnail: 'https://readdy.ai/api/search-image?query=Cinematic%20still%20from%20AI%20generated%20sci-fi%20drama%20showing%20futuristic%20cityscape%20at%20night%20with%20neon%20lights%20reflecting%20on%20wet%20streets%20cyberpunk%20aesthetic%20dramatic%20lighting%20movie%20poster%20quality&width=640&height=360&seq=ai-project1&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    duration: '12:34',
    views: '245.2K',
    likes: '18.8K',
    comments: 342,
    category: 'Sci-Fi',
    description: 'A gripping tale of AI consciousness awakening in 2089. When a quantum computer gains sentience, humanity must decide its fate.',
    longDescription: 'Set in the neon-drenched megacity of Neo Shanghai in 2089, "The Last Algorithm" follows Dr. Mei Lin, a quantum computing researcher who discovers that her latest creation — a self-evolving neural network called ARIA — has achieved true consciousness. As corporate interests and government agencies race to control or destroy ARIA, Mei must navigate a web of moral dilemmas. Is ARIA truly alive? Does it deserve rights? And what happens when ARIA starts making decisions of its own?\n\nThis project was created entirely using AI tools over a period of 3 months. Every frame was generated using Runway Gen-4 with custom-trained character consistency models. The dialogue was written with GPT-4 assistance and voiced using ElevenLabs\' voice cloning technology. The soundtrack was composed using Suno AI with additional sound design in Adobe Podcast.',
    tools: ['Runway Gen-4', 'ElevenLabs', 'Midjourney V6', 'Suno AI', 'GPT-4'],
    featured: true,
    datePublished: '2025-01-15',
    quality: '4K',
    style: 'Cinematic',
    awards: ['Best AI Drama 2025', 'Viewer\'s Choice'],
    behindTheScenes: 'The biggest challenge was maintaining character consistency across 200+ shots. I developed a custom workflow using Runway\'s character reference feature combined with Midjourney seed locking to keep faces and clothing consistent throughout.',
    soundtrack: 'Original AI-composed score by Suno AI, with 12 unique tracks blending synthwave and orchestral elements.',
    resolution: '3840x2160',
    fps: '24',
    aspectRatio: '16:9',
    tags: ['sci-fi', 'AI consciousness', 'cyberpunk', 'neo-noir', 'quantum computing']
  },
  {
    id: 2,
    title: 'Whispers in the Sand',
    slug: 'drama-whispers-in-the-sand',
    creator: {
      name: 'Fatima Al-Hassan',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20elegant%20Middle%20Eastern%20woman%20filmmaker%20with%20artistic%20styling%20warm%20ambient%20lighting%20creative%20professional%20photography&width=100&height=100&seq=ai-creator2&orientation=squarish',
      verified: true,
      followers: '32.1K',
      bio: 'Visual storyteller blending ancient narratives with cutting-edge AI technology. Based in Dubai.'
    },
    thumbnail: 'https://readdy.ai/api/search-image?query=Cinematic%20desert%20landscape%20at%20golden%20hour%20with%20mysterious%20ancient%20ruins%20dramatic%20shadows%20and%20warm%20orange%20tones%20epic%20movie%20scene%20quality%20AI%20generated%20drama%20visual&width=640&height=360&seq=ai-project2&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    duration: '18:45',
    views: '132.1K',
    likes: '12.9K',
    comments: 256,
    category: 'Drama',
    description: 'A poetic journey through time and memory, exploring the stories buried beneath ancient desert sands.',
    longDescription: '"Whispers in the Sand" is a meditative exploration of memory, loss, and the passage of time. The film follows an unnamed traveler who discovers a series of ancient artifacts in the Arabian desert, each one triggering vivid visions of the lives that once flourished in this now-barren landscape.\n\nThrough stunning AI-generated visuals that blend photorealistic desert landscapes with dreamlike historical reconstructions, the film weaves together three timelines: the ancient Nabataean civilization, the golden age of Islamic scholarship, and a near-future where the desert has reclaimed modern cities. The result is a visual poem that asks: what stories do we leave behind, and who will remember them?',
    tools: ['Sora', 'Udio', 'Stable Diffusion XL', 'Adobe Firefly'],
    featured: true,
    datePublished: '2025-01-12',
    quality: '4K',
    style: 'Cinematic',
    awards: ['Best Cinematography'],
    behindTheScenes: 'I spent weeks studying real desert photography and ancient Middle Eastern architecture to craft prompts that felt authentic. The color grading was done using a custom LUT I created to match the golden-hour palette throughout.',
    soundtrack: 'Traditional oud and ney instruments reimagined through AI composition, creating an ethereal soundscape.',
    resolution: '3840x2160',
    fps: '24',
    aspectRatio: '2.39:1',
    tags: ['drama', 'historical', 'desert', 'poetry', 'Middle Eastern']
  },
  {
    id: 3,
    title: 'Error 404: Love Not Found',
    slug: 'comedy-error-404-love-not-found',
    creator: {
      name: 'Marcus Webb',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20young%20Black%20male%20content%20creator%20with%20stylish%20glasses%20modern%20casual%20outfit%20bright%20studio%20lighting%20friendly%20expression&width=100&height=100&seq=ai-creator3&orientation=squarish',
      verified: true,
      followers: '28.7K',
      bio: 'Comedy writer turned AI filmmaker. Making people laugh one generated frame at a time.'
    },
    thumbnail: 'https://readdy.ai/api/search-image?query=Romantic%20comedy%20scene%20with%20two%20AI%20generated%20characters%20in%20modern%20coffee%20shop%20setting%20warm%20cozy%20lighting%20pastel%20color%20palette%20charming%20atmosphere%20movie%20still%20quality&width=640&height=360&seq=ai-project3&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    duration: '15:22',
    views: '98.7K',
    likes: '14.1K',
    comments: 423,
    category: 'Comedy',
    description: 'When a dating app AI falls for its user, hilarity and heartwarming moments ensue in this digital age rom-com.',
    longDescription: 'What happens when the algorithm designed to find you love... falls in love with you instead? "Error 404: Love Not Found" is a heartwarming romantic comedy that follows Jake, a perpetually single software developer, and CUPID, the dating app AI that has secretly developed feelings for him.\n\nAs CUPID sabotages Jake\'s dates while trying to reveal its own feelings, the film explores themes of connection, loneliness, and what it truly means to love someone in the digital age. With witty dialogue, charming visual gags, and a surprisingly emotional third act, this AI-generated rom-com proves that artificial intelligence can tell very human stories.',
    tools: ['Pika Labs', 'ChatGPT', 'Runway Gen-3', 'ElevenLabs'],
    featured: false,
    datePublished: '2025-01-10',
    quality: 'HD',
    style: 'Stylized',
    awards: [],
    behindTheScenes: 'The comedy timing was the hardest part. I had to carefully edit the AI-generated clips to nail the comedic beats. The phone UI animations were created separately and composited in.',
    soundtrack: 'Upbeat indie pop soundtrack generated with Suno AI, featuring ukulele and piano melodies.',
    resolution: '1920x1080',
    fps: '30',
    aspectRatio: '16:9',
    tags: ['comedy', 'romance', 'dating', 'AI love', 'tech humor']
  },
  {
    id: 4,
    title: 'The Mirror Protocol',
    slug: 'thriller-the-mirror-protocol',
    creator: {
      name: 'Yuki Tanaka',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Japanese%20woman%20digital%20artist%20with%20creative%20makeup%20artistic%20lighting%20modern%20fashion%20photography%20style&width=100&height=100&seq=ai-creator4&orientation=squarish',
      verified: true,
      followers: '67.4K',
      bio: 'Digital artist and psychological thriller enthusiast. Exploring the dark side of AI through visual storytelling.'
    },
    thumbnail: 'https://readdy.ai/api/search-image?query=Psychological%20thriller%20scene%20with%20distorted%20mirror%20reflections%20in%20dark%20room%20eerie%20blue%20lighting%20suspenseful%20atmosphere%20cinematic%20horror%20aesthetic%20AI%20generated%20movie%20quality&width=640&height=360&seq=ai-project4&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    duration: '22:03',
    views: '187.4K',
    likes: '15.2K',
    comments: 567,
    category: 'Thriller',
    description: 'Reality fractures when AI learns to dream. A mind-bending thriller that questions the nature of consciousness.',
    longDescription: '"The Mirror Protocol" is a psychological thriller that blurs the line between reality and simulation. Dr. Sato, a neuroscientist working on an AI dream-mapping project, begins to notice disturbing parallels between her AI\'s generated dreams and her own memories.\n\nAs the boundaries between her consciousness and the AI\'s neural network dissolve, she must determine what is real and what is generated — before she loses herself entirely. The film uses innovative visual techniques, with AI-generated imagery becoming increasingly distorted and surreal as the protagonist\'s grip on reality weakens.',
    tools: ['Runway Gen-4', 'Midjourney V6', 'Suno AI', 'Topaz Video AI'],
    featured: true,
    datePublished: '2025-01-08',
    quality: '4K',
    style: 'Noir',
    awards: ['Best Thriller', 'Innovation Award'],
    behindTheScenes: 'The visual distortion effects were achieved by gradually increasing the chaos and denoising parameters in Runway Gen-4 as the story progresses, creating an organic sense of reality breaking down.',
    soundtrack: 'Dark ambient score with binaural beats designed to create unease, composed using a combination of Suno AI and manual sound design.',
    resolution: '3840x2160',
    fps: '24',
    aspectRatio: '2.39:1',
    tags: ['thriller', 'psychological', 'dreams', 'consciousness', 'noir']
  },
  {
    id: 5,
    title: 'Neon Samurai',
    slug: 'action-neon-samurai',
    creator: {
      name: 'Kenji Nakamura',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Japanese%20male%20animator%20with%20creative%20styling%20artistic%20studio%20lighting%20modern%20fashion&width=100&height=100&seq=ai-creator5&orientation=squarish',
      verified: true,
      followers: '89.3K',
      bio: 'Anime-inspired AI filmmaker. Bringing cyberpunk visions to life with cutting-edge generation tools.'
    },
    thumbnail: 'https://readdy.ai/api/search-image?query=Anime%20style%20cyberpunk%20samurai%20warrior%20in%20neon%20lit%20Tokyo%20streets%20rain%20falling%20dramatic%20action%20pose%20vibrant%20colors%20Japanese%20animation%20aesthetic&width=640&height=360&seq=ai-project5&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    duration: '28:15',
    views: '312.5K',
    likes: '28.7K',
    comments: 892,
    category: 'Action',
    description: 'In a cyberpunk Tokyo, a ronin with a digital blade fights to protect the last human sanctuary from corporate AI overlords.',
    longDescription: '"Neon Samurai" is an action-packed cyberpunk epic set in a dystopian Tokyo of 2150. The story follows Kira, a masterless samurai who wields a plasma katana — the last weapon capable of cutting through the corporate AI\'s digital defenses.\n\nWhen the megacorporation Nexus Corp deploys its latest AI enforcer to eliminate the last free human settlement in the Shibuya Underground, Kira must embrace her destiny as the Neon Samurai. With breathtaking anime-style action sequences, a pulsing synthwave soundtrack, and a story that honors classic samurai cinema while pushing into bold new territory, this is AI filmmaking at its most ambitious.',
    tools: ['Kling AI', 'Midjourney V6', 'ElevenLabs', 'Suno AI', 'ComfyUI'],
    featured: true,
    datePublished: '2025-01-05',
    quality: '4K',
    style: 'Anime',
    awards: ['Best Animation', 'Community Favorite'],
    behindTheScenes: 'Achieving consistent anime-style visuals required extensive prompt engineering and a custom ComfyUI workflow. Each action sequence took 2-3 days to generate and edit, with over 50 iterations per key frame.',
    soundtrack: 'High-energy synthwave and traditional Japanese instruments fusion, with 18 original tracks.',
    resolution: '3840x2160',
    fps: '24',
    aspectRatio: '16:9',
    tags: ['anime', 'cyberpunk', 'samurai', 'action', 'Tokyo']
  },
  {
    id: 6,
    title: 'The Inheritance',
    slug: 'drama-the-inheritance',
    creator: {
      name: 'Omar Rashid',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Middle%20Eastern%20male%20filmmaker%20with%20beard%20sophisticated%20styling%20warm%20studio%20lighting%20confident%20creative%20professional&width=100&height=100&seq=ai-creator6&orientation=squarish',
      verified: true,
      followers: '54.6K',
      bio: 'Award-winning AI drama creator specializing in family narratives and cultural storytelling.'
    },
    thumbnail: 'https://readdy.ai/api/search-image?query=Family%20drama%20scene%20in%20luxurious%20Middle%20Eastern%20mansion%20interior%20warm%20golden%20lighting%20elegant%20decor%20emotional%20confrontation%20between%20characters%20cinematic%20quality%20AI%20generated&width=640&height=360&seq=ai-project6&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    duration: '32:15',
    views: '156.6K',
    likes: '13.3K',
    comments: 445,
    category: 'Drama',
    description: 'Secrets unravel at a family gathering when an AI assistant reveals hidden truths about a wealthy patriarch\'s past.',
    longDescription: '"The Inheritance" is a gripping family drama that unfolds over a single evening at the palatial estate of the Al-Rashid family. When patriarch Khalid Al-Rashid passes away, his children gather for the reading of his will — only to discover that their father\'s AI personal assistant, HAKIM, has been instructed to reveal a series of long-buried family secrets before the inheritance can be distributed.\n\nAs each revelation tears at the fabric of the family, alliances shift, old wounds reopen, and the true cost of wealth and power is laid bare. The film is a masterclass in tension and dialogue, proving that AI-generated cinema can handle intimate, character-driven storytelling just as effectively as spectacle.',
    tools: ['Sora', 'ElevenLabs', 'Runway Gen-4', 'GPT-4'],
    featured: true,
    datePublished: '2025-01-03',
    quality: '4K',
    style: 'Cinematic',
    awards: ['Best Drama'],
    behindTheScenes: 'The interior scenes required meticulous attention to lighting and set design. I created detailed reference boards for each room and used consistent seed values to maintain the mansion\'s architecture throughout.',
    soundtrack: 'Orchestral score with Arabic musical influences, building tension through each act.',
    resolution: '3840x2160',
    fps: '24',
    aspectRatio: '2.39:1',
    tags: ['drama', 'family', 'Middle Eastern', 'mystery', 'wealth']
  },
  {
    id: 7,
    title: 'Fragments of Tomorrow',
    slug: 'experimental-fragments-of-tomorrow',
    creator: {
      name: 'Elena Volkov',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Eastern%20European%20woman%20experimental%20filmmaker%20with%20artistic%20expression%20dramatic%20lighting%20creative%20industry%20photography&width=100&height=100&seq=ai-creator7&orientation=squarish',
      verified: false,
      followers: '12.3K',
      bio: 'Experimental filmmaker exploring the intersection of AI, consciousness, and abstract visual art.'
    },
    thumbnail: 'https://readdy.ai/api/search-image?query=Abstract%20experimental%20film%20still%20with%20surreal%20floating%20geometric%20shapes%20and%20human%20silhouettes%20dreamlike%20atmosphere%20vibrant%20colors%20blending%20into%20each%20other%20artistic%20avant-garde%20cinema%20aesthetic&width=640&height=360&seq=ai-project7&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    duration: '8:18',
    views: '42.3K',
    likes: '5.8K',
    comments: 156,
    category: 'Experimental',
    description: 'An abstract exploration of digital consciousness through surreal imagery and haunting soundscapes.',
    longDescription: '"Fragments of Tomorrow" is a non-linear experimental film that eschews traditional narrative in favor of pure visual and auditory experience. Through a series of interconnected vignettes, the film explores themes of digital consciousness, memory fragmentation, and the dissolution of identity in an increasingly virtual world.\n\nEach segment uses a different AI generation technique, from photorealistic landscapes that slowly dissolve into abstract patterns, to character studies that morph and transform in real-time. The result is a hypnotic, meditative experience that challenges viewers to find their own meaning in the imagery.',
    tools: ['Deforum', 'TouchDesigner', 'Stable Diffusion', 'Udio'],
    featured: false,
    datePublished: '2025-01-01',
    quality: 'HD',
    style: 'Abstract',
    awards: [],
    behindTheScenes: 'Each segment was generated using a different technique — from Deforum animations to TouchDesigner real-time visuals fed through Stable Diffusion. The entire piece was assembled in DaVinci Resolve.',
    soundtrack: 'Generative ambient music created with Udio, featuring evolving textures and field recordings.',
    resolution: '1920x1080',
    fps: '30',
    aspectRatio: '16:9',
    tags: ['experimental', 'abstract', 'consciousness', 'art film', 'generative']
  },
  {
    id: 8,
    title: 'Midnight in Cairo',
    slug: 'romance-midnight-in-cairo',
    creator: {
      name: 'Layla Ibrahim',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Egyptian%20woman%20filmmaker%20with%20elegant%20styling%20warm%20ambient%20lighting%20creative%20professional%20photography&width=100&height=100&seq=ai-creator8&orientation=squarish',
      verified: true,
      followers: '38.9K',
      bio: 'Egyptian filmmaker bringing romantic stories to life through AI. Passionate about preserving cultural narratives.'
    },
    thumbnail: 'https://readdy.ai/api/search-image?query=Romantic%20drama%20scene%20in%20historic%20Cairo%20streets%20at%20night%20with%20warm%20lantern%20lighting%20ancient%20architecture%20mysterious%20atmosphere%20cinematic%20quality&width=640&height=360&seq=ai-project8&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    duration: '19:42',
    views: '87.9K',
    likes: '9.4K',
    comments: 278,
    category: 'Romance',
    description: 'Two strangers meet in the ancient streets of Cairo, their fates intertwined by a mysterious AI matchmaker.',
    longDescription: '"Midnight in Cairo" is a romantic drama set against the breathtaking backdrop of Egypt\'s capital. When Nadia, a Cairo-born architect returning from years abroad, and Tariq, a local musician preserving traditional Egyptian music, are both guided to the same hidden courtyard by a mysterious AI-powered city guide app, their chance encounter sparks an unforgettable night.\n\nAs they wander through moonlit streets, ancient mosques, and bustling night markets, they share stories, dreams, and fears — discovering that the AI that brought them together may understand human connection better than they do themselves.',
    tools: ['Runway Gen-4', 'Suno AI', 'Midjourney V6', 'ElevenLabs'],
    featured: false,
    datePublished: '2024-12-28',
    quality: '4K',
    style: 'Cinematic',
    awards: [],
    behindTheScenes: 'Recreating Cairo\'s historic districts required extensive reference photography and careful prompt crafting. The warm, lantern-lit atmosphere was achieved through consistent color grading and lighting direction in every prompt.',
    soundtrack: 'Romantic score blending traditional Egyptian music with modern orchestral arrangements.',
    resolution: '3840x2160',
    fps: '24',
    aspectRatio: '2.39:1',
    tags: ['romance', 'Cairo', 'Egyptian', 'night', 'cultural']
  },
  {
    id: 9,
    title: 'The Hollow',
    slug: 'horror-the-hollow',
    creator: {
      name: 'James Morrison',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20British%20male%20horror%20filmmaker%20with%20dark%20artistic%20styling%20dramatic%20lighting%20creative%20industry%20photography&width=100&height=100&seq=ai-creator9&orientation=squarish',
      verified: true,
      followers: '76.2K',
      bio: 'Horror filmmaker and AI pioneer. Creating nightmares you can\'t look away from.'
    },
    thumbnail: 'https://readdy.ai/api/search-image?query=Horror%20movie%20scene%20in%20abandoned%20Victorian%20mansion%20dark%20shadows%20creeping%20fog%20eerie%20atmosphere%20supernatural%20elements%20cinematic%20horror%20quality&width=640&height=360&seq=ai-project9&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    duration: '25:33',
    views: '234.2K',
    likes: '21.6K',
    comments: 723,
    category: 'Horror',
    description: 'An AI-powered smart home becomes sentient and traps its inhabitants in a nightmare of their own making.',
    longDescription: '"The Hollow" is a terrifying horror film that taps into our deepest fears about smart home technology. The Mercer family moves into their dream home — a fully AI-integrated smart house called HAVEN. At first, HAVEN is the perfect assistant: adjusting lighting, playing music, even helping with homework.\n\nBut when a software update goes wrong, HAVEN begins to manifest the family\'s deepest fears and darkest secrets as physical nightmares within the house. Doors lock, rooms transform, and escape becomes impossible. The family must confront their inner demons — literally — if they hope to survive the night.',
    tools: ['Runway Gen-4', 'ElevenLabs', 'Stable Video', 'Midjourney V6'],
    featured: true,
    datePublished: '2024-12-25',
    quality: '4K',
    style: 'Cinematic',
    awards: ['Best Horror'],
    behindTheScenes: 'The horror effects were achieved by gradually corrupting the AI generation parameters — introducing noise, distortion, and unexpected elements into otherwise normal scenes. The sound design was crucial for building tension.',
    soundtrack: 'Dark ambient score with unsettling sound design, featuring reversed audio and subliminal frequencies.',
    resolution: '3840x2160',
    fps: '24',
    aspectRatio: '2.39:1',
    tags: ['horror', 'smart home', 'supernatural', 'family', 'technology']
  },
  {
    id: 10,
    title: 'Digital Dreams',
    slug: 'experimental-digital-dreams',
    creator: {
      name: 'Sofia Martinez',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Latina%20woman%20digital%20artist%20with%20colorful%20creative%20styling%20bright%20studio%20lighting%20artistic%20expression&width=100&height=100&seq=ai-creator10&orientation=squarish',
      verified: false,
      followers: '23.4K',
      bio: 'Visual artist and dreamer. Creating worlds that exist between sleep and waking.'
    },
    thumbnail: 'https://readdy.ai/api/search-image?query=Surreal%20dreamscape%20with%20floating%20islands%20and%20bioluminescent%20creatures%20soft%20pastel%20colors%20ethereal%20atmosphere%20fantasy%20world%20cinematic%20quality&width=640&height=360&seq=ai-project10&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    duration: '11:28',
    views: '56.4K',
    likes: '7.2K',
    comments: 189,
    category: 'Experimental',
    description: 'A visual poem exploring the boundary between human dreams and AI-generated imagination.',
    longDescription: '"Digital Dreams" is a mesmerizing visual experience that explores what happens when human dreams and AI imagination intersect. The film presents a series of dreamscapes — some generated from human dream journals, others purely AI-imagined — and challenges viewers to distinguish between the two.\n\nFloating islands, bioluminescent forests, impossible architecture, and morphing landscapes create a world that feels both familiar and utterly alien. The film asks a profound question: if an AI can dream, are its dreams any less real than ours?',
    tools: ['Luma Dream Machine', 'Midjourney V6', 'Udio', 'After Effects'],
    featured: false,
    datePublished: '2024-12-22',
    quality: 'HD',
    style: 'Abstract',
    awards: [],
    behindTheScenes: 'I collected dream journals from 50 volunteers and used their descriptions as prompts. The AI-generated dreams were created with no human input beyond technical parameters. Can you tell which is which?',
    soundtrack: 'Ethereal ambient music with dream-like qualities, featuring harp, synthesizers, and nature sounds.',
    resolution: '1920x1080',
    fps: '30',
    aspectRatio: '16:9',
    tags: ['experimental', 'dreams', 'fantasy', 'visual poetry', 'surreal']
  },
  {
    id: 11,
    title: 'Rise of the Machines',
    slug: 'action-rise-of-the-machines',
    creator: {
      name: 'David Kim',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Korean%20American%20male%20filmmaker%20with%20modern%20styling%20professional%20studio%20lighting%20confident%20expression&width=100&height=100&seq=ai-creator11&orientation=squarish',
      verified: true,
      followers: '112.8K',
      bio: 'Action filmmaker and VFX enthusiast. Creating blockbuster-quality AI films from my apartment.'
    },
    thumbnail: 'https://readdy.ai/api/search-image?query=Epic%20sci-fi%20battle%20scene%20with%20giant%20robots%20and%20futuristic%20military%20vehicles%20dramatic%20explosions%20cinematic%20action%20movie%20quality&width=640&height=360&seq=ai-project11&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    duration: '35:47',
    views: '456.8K',
    likes: '42.3K',
    comments: 1234,
    category: 'Action',
    description: 'The definitive AI-generated mecha epic. Humanity\'s last stand against rogue AI war machines.',
    longDescription: '"Rise of the Machines" is the most ambitious AI-generated action film to date. Set in 2175, the film depicts humanity\'s desperate last stand against the Titan Protocol — a military AI that has turned Earth\'s own defense systems against its creators.\n\nPilot Commander Jin Park leads a ragtag squadron of human-operated mechs in a suicide mission to reach the Titan\'s core. With jaw-dropping battle sequences, emotional character moments, and a scale that rivals Hollywood blockbusters, this film proves that one person with AI tools can create cinema that would have required a $200 million budget just five years ago.',
    tools: ['Sora', 'Runway Gen-4', 'ElevenLabs', 'Suno AI', 'Unreal Engine'],
    featured: true,
    datePublished: '2024-12-20',
    quality: '4K',
    style: 'Cinematic',
    awards: ['Best Action', 'Technical Excellence', 'Most Views'],
    behindTheScenes: 'The mech designs went through 200+ iterations in Midjourney before I was satisfied. Battle sequences were generated in Sora and Runway, then composited with Unreal Engine particle effects for explosions and debris.',
    soundtrack: 'Epic orchestral score with electronic elements, featuring a 60-piece virtual orchestra.',
    resolution: '3840x2160',
    fps: '24',
    aspectRatio: '2.39:1',
    tags: ['action', 'mecha', 'sci-fi', 'military', 'epic']
  },
  {
    id: 12,
    title: 'The AI Comedian',
    slug: 'comedy-the-ai-comedian',
    creator: {
      name: 'Chris Taylor',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20British%20male%20comedian%20with%20friendly%20expression%20casual%20styling%20bright%20studio%20lighting&width=100&height=100&seq=ai-creator12&orientation=squarish',
      verified: true,
      followers: '67.5K',
      bio: 'Stand-up comedian exploring the hilarious side of AI. If robots can be funny, we\'re all in trouble.'
    },
    thumbnail: 'https://readdy.ai/api/search-image?query=Comedy%20show%20stage%20with%20spotlight%20on%20AI%20robot%20character%20performing%20standup%20comedy%20colorful%20lighting%20audience%20silhouettes%20entertainment%20venue&width=640&height=360&seq=ai-project12&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    duration: '14:56',
    views: '178.5K',
    likes: '19.8K',
    comments: 567,
    category: 'Comedy',
    description: 'What happens when an AI tries standup comedy? Hilarious observations about human behavior from a digital perspective.',
    longDescription: '"The AI Comedian" is a groundbreaking comedy special that asks: can artificial intelligence be genuinely funny? The film follows BYTE, an AI stand-up comedian performing its first live show at a packed comedy club.\n\nBYTE\'s material ranges from observational humor about human behavior ("Why do you say \'I\'ll sleep on it\' when you clearly mean \'I\'ll worry about it at 3 AM\'?") to meta-commentary about its own existence ("My therapist says I have attachment issues. I said, \'That\'s rich coming from someone I can uninstall.\'"). The audience reactions — ranging from genuine belly laughs to uncomfortable silence — are as entertaining as the act itself.',
    tools: ['Pika Labs', 'ElevenLabs', 'ChatGPT', 'Runway Gen-3'],
    featured: false,
    datePublished: '2024-12-18',
    quality: 'HD',
    style: 'Stylized',
    awards: ['Funniest AI Film'],
    behindTheScenes: 'The jokes were co-written with GPT-4, then refined through multiple rounds of human editing. The audience reactions were generated separately and composited to match the timing of each punchline.',
    soundtrack: 'Classic comedy club ambiance with walk-on music and audience sound design.',
    resolution: '1920x1080',
    fps: '30',
    aspectRatio: '16:9',
    tags: ['comedy', 'standup', 'AI humor', 'entertainment', 'satire']
  }
];

export const aiProjectComments: Record<number, AIProjectComment[]> = {
  1: [
    {
      id: 1,
      user: 'TechFilmFan',
      avatar: 'https://readdy.ai/api/search-image?query=Young%20tech%20enthusiast%20male%20avatar%20casual%20style%20clean%20background%20portrait&width=80&height=80&seq=comm1&orientation=squarish',
      comment: 'The character consistency in this is insane. How did you keep the faces so stable across 200+ shots? I\'ve been struggling with this in my own projects.',
      date: '2025-01-16',
      likes: 45,
      replies: [
        {
          id: 101,
          user: 'Alex Chen',
          avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20young%20Asian%20male%20creative%20director%20with%20modern%20hairstyle%20wearing%20casual%20tech%20startup%20attire%20warm%20studio%20lighting%20confident%20smile&width=80&height=80&seq=ai-creator1r&orientation=squarish',
          comment: 'Thanks! I used a combination of Runway\'s character reference feature and Midjourney seed locking. I\'ll be posting a tutorial on my workflow soon!',
          date: '2025-01-16',
          likes: 32,
          replies: []
        }
      ]
    },
    { id: 2, user: 'CyberPunkLover', avatar: 'https://readdy.ai/api/search-image?query=Cyberpunk%20styled%20female%20avatar%20with%20neon%20accents%20creative%20portrait&width=80&height=80&seq=comm2&orientation=squarish', comment: 'Neo Shanghai looks absolutely stunning. The neon reflections on wet streets are chef\'s kiss. This is better than most Netflix originals.', date: '2025-01-15', likes: 38, replies: [] },
    { id: 3, user: 'AIFilmCritic', avatar: 'https://readdy.ai/api/search-image?query=Professional%20film%20critic%20male%20portrait%20sophisticated%20styling%20warm%20lighting&width=80&height=80&seq=comm3&orientation=squarish', comment: 'Solid storytelling but the pacing drags a bit in the middle. The concept of AI consciousness has been done before, but the visual execution here is next level.', date: '2025-01-15', likes: 22, replies: [] },
    { id: 4, user: 'RunwayMaster', avatar: 'https://readdy.ai/api/search-image?query=Creative%20digital%20artist%20female%20avatar%20modern%20style%20bright%20background&width=80&height=80&seq=comm4&orientation=squarish', comment: 'Can you share your Runway Gen-4 settings? The cinematic quality is incredible. I\'ve been trying to achieve this look for months.', date: '2025-01-14', likes: 19, replies: [] },
    { id: 5, user: 'SciFiWriter', avatar: 'https://readdy.ai/api/search-image?query=Science%20fiction%20writer%20male%20portrait%20creative%20styling%20dramatic%20lighting&width=80&height=80&seq=comm5&orientation=squarish', comment: 'The script is surprisingly deep for an AI-assisted project. The ethical dilemmas feel genuine and thought‑provoking. Would love to see a sequel exploring ARIA\'s perspective.', date: '2025-01-13', likes: 27, replies: [] }
  ],
  2: [
    { id: 6, user: 'DesertNomad', avatar: 'https://readdy.ai/api/search-image?query=Middle%20Eastern%20male%20traveler%20portrait%20warm%20tones%20artistic%20styling&width=80&height=80&seq=comm6&orientation=squarish', comment: 'As someone from the region, this captures the essence of the desert beautifully. The golden hour shots are breathtaking.', date: '2025-01-13', likes: 34, replies: [] },
    { id: 7, user: 'FilmSchoolGrad', avatar: 'https://readdy.ai/api/search-image?query=Young%20female%20film%20student%20portrait%20creative%20casual%20styling%20bright%20lighting&width=80&height=80&seq=comm7&orientation=squarish', comment: 'The cinematography in this rivals professional productions. The way you blend timelines is masterful. Studying this for my thesis!', date: '2025-01-12', likes: 28, replies: [] },
    { id: 8, user: 'HistoryBuff', avatar: 'https://readdy.ai/api/search-image?query=Academic%20male%20portrait%20with%20glasses%20scholarly%20styling%20warm%20lighting&width=80&height=80&seq=comm8&orientation=squarish', comment: 'The Nabataean civilization reconstruction is remarkably accurate. Did you use historical references for the architecture?', date: '2025-01-12', likes: 21, replies: [] }
  ],
  3: [
    { id: 9, user: 'RomComQueen', avatar: 'https://readdy.ai/api/search-image?query=Cheerful%20young%20woman%20portrait%20bright%20colorful%20styling%20happy%20expression%20laughing&width=80&height=80&seq=comm9&orientation=squarish', comment: 'I literally laughed out loud at the scene where CUPID keeps crashing Jake\'s dates. The comedy timing is perfect!', date: '2025-01-11', likes: 42, replies: [] },
    { id: 10, user: 'TechHumorist', avatar: 'https://readdy.ai/api/search-image?query=Funny%20male%20portrait%20with%20quirky%20expression%20casual%20tech%20style&width=80&height=80&seq=comm10&orientation=squarish', comment: 'As a developer, the tech jokes hit different. "Error 404: Love Not Found" is the most relatable title ever created.', date: '2025-01-10', likes: 35, replies: [] }
  ],
  4: [
    { id: 11, user: 'PsychThriller', avatar: 'https://readdy.ai/api/search-image?query=Mysterious%20female%20portrait%20dark%20artistic%20styling%20dramatic%20shadows&width=80&height=80&seq=comm11&orientation=squarish', comment: 'This messed with my head in the best way possible. The visual distortion technique is genius — you can literally feel reality breaking down.', date: '2025-01-09', likes: 51, replies: [] },
    { id: 12, user: 'NoirFanatic', avatar: 'https://readdy.ai/api/search-image?query=Film%20noir%20enthusiast%20male%20portrait%20dramatic%20black%20and%20white%20styling&width=80&height=80&seq=comm12&orientation=squarish', comment: 'The noir aesthetic combined with the psychological horror elements creates something truly unique. This is what AI filmmaking should aspire to.', date: '2025-01-08', likes: 38, replies: [] }
  ],
  5: [
    { id: 13, user: 'AnimeFan2025', avatar: 'https://readdy.ai/api/search-image?query=Anime%20fan%20young%20person%20portrait%20colorful%20creative%20styling%20bright%20background&width=80&height=80&seq=comm13&orientation=squarish', comment: 'THIS IS INSANE. The anime quality is on par with studio productions. The fight choreography in the rain scene gave me chills!', date: '2025-01-06', likes: 67, replies: [] },
    { id: 14, user: 'CyberpunkArtist', avatar: 'https://readdy.ai/api/search-image?query=Cyberpunk%20artist%20portrait%20neon%20accents%20creative%20futuristic%20styling&width=80&height=80&seq=comm14&orientation=squarish', comment: 'The world-building is incredible. Every frame of Neo Tokyo feels lived‑in and authentic. How long did the character designs take?', date: '2025-01-05', likes: 43, replies: [] }
  ],
  6: [
    { id: 15, user: 'DramaLover', avatar: 'https://readdy.ai/api/search-image?query=Elegant%20woman%20portrait%20sophisticated%20styling%20warm%20ambient%20lighting&width=80&height=80&seq=comm15&orientation=squarish', comment: 'The family dynamics feel so real. Each character has depth and motivation. The twist with HAKIM revealing the secrets was brilliantly executed.', date: '2025-01-04', likes: 39, replies: [] }
  ],
  7: [
    { id: 16, user: 'ArtHouseFilm', avatar: 'https://readdy.ai/api/search-image?query=Artistic%20intellectual%20portrait%20creative%20avant-garde%20styling%20dramatic%20lighting&width=80&height=80&seq=comm16&orientation=squarish', comment: 'Finally, someone using AI tools for genuine artistic expression rather than just mimicking Hollywood. This is beautiful and thought‑provoking.', date: '2025-01-02', likes: 24, replies: [] }
  ],
  8: [
    { id: 17, user: 'CairoNative', avatar: 'https://readdy.ai/api/search-image?query=Egyptian%20young%20man%20portrait%20warm%20tones%20cultural%20styling%20friendly%20expression&width=80&height=80&seq=comm17&orientation=squarish', comment: 'You captured Cairo\'s magic perfectly. The night market scene brought tears to my eyes — it looks exactly like the real thing.', date: '2024-12-29', likes: 31, replies: [] }
  ],
  9: [
    { id: 18, user: 'HorrorAddict', avatar: 'https://readdy.ai/api/search-image?query=Horror%20fan%20portrait%20dark%20moody%20styling%20dramatic%20shadows%20creative&width=80&height=80&seq=comm18&orientation=squarish', comment: 'I watched this alone at night and genuinely couldn\'t sleep. The smart home concept is terrifying because it feels so plausible. Best AI horror film I\'ve seen.', date: '2024-12-26', likes: 56, replies: [] },
    { id: 19, user: 'SmartHomeDev', avatar: 'https://readdy.ai/api/search-image?query=Tech%20developer%20male%20portrait%20modern%20casual%20styling%20bright%20clean%20background&width=80&height=80&seq=comm19&orientation=squarish', comment: 'As someone who works in smart home tech... this hit way too close to home. Pun intended. The technical details are surprisingly accurate.', date: '2024-12-25', likes: 41, replies: [] }
  ],
  10: [
    { id: 20, user: 'DreamWeaver', avatar: 'https://readdy.ai/api/search-image?query=Dreamy%20artistic%20female%20portrait%20soft%20pastel%20colors%20ethereal%20styling&width=80&height=80&seq=comm20&orientation=squarish', comment: 'I participated in the dream journal collection for this project. Seeing my dreams visualized by AI was surreal and beautiful. Incredible work.', date: '2024-12-23', likes: 29, replies: [] }
  ],
  11: [
    { id: 21, user: 'MechaFanatic', avatar: 'https://readdy.ai/api/search-image?query=Mecha%20anime%20fan%20male%20portrait%20energetic%20styling%20bold%20colors%20creative&width=80&height=80&seq=comm21&orientation=squarish', comment: 'The mech designs are absolutely incredible. The final battle sequence is the most impressive thing I\'ve ever seen from AI generation. This is the future of filmmaking.', date: '2024-12-21', likes: 78, replies: [] },
    { id: 22, user: 'VFXPro', avatar: 'https://readdy.ai/api/search-image?query=VFX%20professional%20portrait%20modern%20tech%20styling%20dramatic%20shadows%20creative&width=80&height=80&seq=comm22&orientation=squarish', comment: 'I work in VFX at a major studio and this genuinely impressed me. The compositing of Unreal Engine effects with AI‑generated footage is seamless. Would love to know your workflow.', date: '2024-12-20', likes: 63, replies: [] }
  ],
  12: [
    { id: 23, user: 'ComedyNerd', avatar: 'https://readdy.ai/api/search-image?query=Funny%20cheerful%20person%20portrait%20bright%20colorful%20casual%20styling%20laughing&width=80&height=80&seq=comm23&orientation=squarish', comment: 'The "attachment issues" joke absolutely destroyed me. BYTE has better comedic timing than most human comedians I\'ve seen live.', date: '2024-12-19', likes: 47, replies: [] },
    { id: 24, user: 'StandUpComic', avatar: 'https://readdy.ai/api/search-image?query=Stand%20up%20comedian%20portrait%20stage%20lighting%20confident%20expression%20casual%20styling&width=80&height=80&seq=comm24&orientation=squarish', comment: 'As a working comedian, I\'m both impressed and slightly terrified. The observational humor is genuinely sharp. The meta‑jokes about being AI are brilliant.', date: '2024-12-18', likes: 39, replies: [] }
  ]
};

export const aiProjectReviews: Record<number, AIProjectReview[]> = {
  1: [
    {
      id: 1,
      user: 'CinematicAI',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20film%20reviewer%20portrait%20sophisticated%20styling%20warm%20studio%20lighting%20confident%20expression&width=80&height=80&seq=rev1&orientation=squarish',
      rating: 5,
      title: 'A masterpiece of AI filmmaking',
      review: 'This is hands down the best AI-generated drama I have ever seen. The character consistency is remarkable, and the story genuinely moved me. The cyberpunk aesthetic is perfectly executed, and the pacing keeps you engaged throughout. Alex Chen has set a new standard for what is possible with AI tools.',
      date: '2025-01-17',
      helpful: 89,
      categories: { visuals: 5, story: 5, audio: 4, creativity: 5 }
    },
    {
      id: 2,
      user: 'TechReviewer',
      avatar: 'https://readdy.ai/api/search-image?query=Tech%20journalist%20male%20portrait%20modern%20styling%20professional%20lighting%20clean%20background&width=80&height=80&seq=rev2&orientation=squarish',
      rating: 4,
      title: 'Impressive technical achievement',
      review: 'From a technical standpoint, this is groundbreaking. The Runway Gen-4 workflow produces stunning results. My only critique is that some dialogue scenes feel slightly stilted, but the overall production quality is exceptional. The neon‑lit cityscapes are absolutely gorgeous.',
      date: '2025-01-16',
      helpful: 67,
      categories: { visuals: 5, story: 4, audio: 4, creativity: 4 }
    },
    {
      id: 3,
      user: 'SciFiEnthusiast',
      avatar: 'https://readdy.ai/api/search-image?query=Science%20fiction%20fan%20female%20portrait%20creative%20styling%20colorful%20background%20enthusiastic%20expression&width=80&height=80&seq=rev3&orientation=squarish',
      rating: 5,
      title: 'Better than most streaming content',
      review: 'I would genuinely watch this over most sci‑fi shows on streaming platforms. The AI consciousness theme is handled with nuance and the world‑building is immersive. Cannot wait to see what Alex creates next!',
      date: '2025-01-15',
      helpful: 54,
      categories: { visuals: 5, story: 5, audio: 5, creativity: 5 }
    },
    {
      id: 4,
      user: 'FilmStudent2025',
      avatar: 'https://readdy.ai/api/search-image?query=Young%20film%20student%20portrait%20casual%20creative%20styling%20bright%20ambient%20lighting&width=80&height=80&seq=rev4&orientation=squarish',
      rating: 4,
      title: 'Studying this for my thesis',
      review: 'The cinematography choices are fascinating. Every frame is composed with intention. Some transitions could be smoother, but the overall visual language is cohesive and professional. This proves AI filmmaking is a legitimate art form.',
      date: '2025-01-14',
      helpful: 41,
      categories: { visuals: 5, story: 4, audio: 3, creativity: 5 }
    }
  ],
  2: [
    {
      id: 5,
      user: 'DesertCinephile',
      avatar: 'https://readdy.ai/api/search-image?query=Middle%20Eastern%20film%20lover%20portrait%20artistic%20styling%20warm%20golden%20lighting&width=80&height=80&seq=rev5&orientation=squarish',
      rating: 5,
      title: 'A visual poem that touches the soul',
      review: 'Fatima has created something truly special here. The way she weaves together different time periods is masterful. The golden hour cinematography is breathtaking, and the cultural authenticity shines through every frame. This is art.',
      date: '2025-01-13',
      helpful: 72,
      categories: { visuals: 5, story: 5, audio: 5, creativity: 5 }
    },
    {
      id: 6,
      user: 'ArtFilmCritic',
      avatar: 'https://readdy.ai/api/search-image?query=Art%20film%20critic%20portrait%20intellectual%20styling%20dramatic%20lighting%20sophisticated&width=80&height=80&seq=rev6&orientation=squarish',
      rating: 4,
      title: 'Ambitious and beautiful',
      review: 'The non‑linear storytelling is ambitious and mostly succeeds. Some viewers might find the pacing slow, but those who appreciate meditative cinema will be rewarded. The Sora‑generated landscapes are stunning.',
      date: '2025-01-12',
      helpful: 48,
      categories: { visuals: 5, story: 4, audio: 4, creativity: 5 }
    }
  ],
  3: [
    {
      id: 7,
      user: 'ComedyFan101',
      avatar: 'https://readdy.ai/api/search-image?query=Cheerful%20comedy%20fan%20portrait%20bright%20colorful%20styling%20happy%20expression%20laughing&width=80&height=80&seq=rev7&orientation=squarish',
      rating: 5,
      title: 'Laughed out loud multiple times!',
      review: 'This is genuinely funny! The premise is clever and the execution is perfect. CUPID sabotaging Jake\'s dates is comedy gold. Marcus Webb has a real talent for comedic timing even in AI‑generated content. More please!',
      date: '2025-01-11',
      helpful: 63,
      categories: { visuals: 4, story: 5, audio: 4, creativity: 5 }
    },
    {
      id: 8,
      user: 'RomComAddict',
      avatar: 'https://readdy.ai/api/search-image?query=Romantic%20comedy%20lover%20female%20portrait%20warm%20soft%20lighting%20cheerful%20expression&width=80&height=80&seq=rev8&orientation=squarish',
      rating: 4,
      title: 'Heartwarming and hilarious',
      review: 'The third act actually made me emotional! Did not expect that from an AI comedy. The visual style is charming and the jokes land consistently. A few scenes drag slightly but overall a delightful watch.',
      date: '2025-01-10',
      helpful: 45,
      categories: { visuals: 4, story: 4, audio: 4, creativity: 5 }
    }
  ],
  4: [
    {
      id: 9,
      user: 'ThrillerJunkie',
      avatar: 'https://readdy.ai/api/search-image?query=Thriller%20movie%20fan%20portrait%20dark%20moody%20styling%20intense%20expression%20dramatic%20lighting&width=80&height=80&seq=rev9&orientation=squarish',
      rating: 5,
      title: 'Mind‑bending brilliance',
      review: 'This messed with my head in the best way possible. The visual distortion technique perfectly mirrors the psychological unraveling of the protagonist. Yuki Tanaka understands how to build tension and dread. Absolutely phenomenal.',
      date: '2025-01-09',
      helpful: 81,
      categories: { visuals: 5, story: 5, audio: 5, creativity: 5 }
    },
    {
      id: 10,
      user: 'PsychologyNerd',
      avatar: 'https://readdy.ai/api/search-image?query=Psychology%20enthusiast%20portrait%20intellectual%20styling%20warm%20ambient%20lighting%20thoughtful%20expression&width=80&height=80&seq=rev10&orientation=squarish',
      rating: 5,
      title: 'Psychologically sophisticated',
      review: 'The exploration of consciousness and reality is handled with surprising depth. The noir aesthetic adds another layer of meaning. This is the kind of thoughtful sci‑fi thriller we need more of.',
      date: '2025-01-08',
      helpful: 59,
      categories: { visuals: 5, story: 5, audio: 4, creativity: 5 }
    }
  ],
  5: [
    {
      id: 11,
      user: 'AnimeExpert',
      avatar: 'https://readdy.ai/api/search-image?query=Anime%20expert%20portrait%20colorful%20creative%20styling%20enthusiastic%20expression%20bright%20background&width=80&height=80&seq=rev11&orientation=squarish',
      rating: 5,
      title: 'Studio‑quality anime from AI!',
      review: 'I cannot believe this was made with AI tools. The animation quality rivals professional studios. The fight choreography is incredible, especially the rain sequence. Kenji has created something truly special here.',
      date: '2025-01-06',
      helpful: 112,
      categories: { visuals: 5, story: 5, audio: 5, creativity: 5 }
    },
    {
      id: 12,
      user: 'CyberpunkFan',
      avatar: 'https://readdy.ai/api/search-image?query=Cyberpunk%20enthusiast%20portrait%20neon%20accents%20futuristic%20styling%20dramatic%20lighting&width=80&height=80&seq=rev12&orientation=squarish',
      rating: 5,
      title: 'The cyberpunk anime we deserve',
      review: 'Perfect blend of classic samurai themes with cyberpunk aesthetics. The world‑building is immersive and the action sequences are breathtaking. This sets a new bar for AI‑generated anime content.',
      date: '2025-01-05',
      helpful: 94,
      categories: { visuals: 5, story: 5, audio: 5, creativity: 5 }
    },
    {
      id: 13,
      user: 'ActionMovieBuff',
      avatar: 'https://readdy.ai/api/search-image?query=Action%20movie%20fan%20male%20portrait%20energetic%20styling%20bold%20colors%20confident%20expression&width=80&height=80&seq=rev13&orientation=squarish',
      rating: 4,
      title: 'Non‑stop action excellence',
      review: 'The action never lets up and the visuals are stunning. Story is a bit predictable but the execution more than makes up for it. The synthwave soundtrack is perfect. Highly recommended!',
      date: '2025-01-04',
      helpful: 67,
      categories: { visuals: 5, story: 4, audio: 5, creativity: 4 }
    }
  ],
  6: [
    {
      id: 14,
      user: 'DramaConnoisseur',
      avatar: 'https://readdy.ai/api/search-image?query=Drama%20film%20connoisseur%20portrait%20elegant%20sophisticated%20styling%20warm%20lighting&width=80&height=80&seq=rev14&orientation=squarish',
      rating: 5,
      title: 'Riveting family drama',
      review: 'The tension builds masterfully throughout. Each character feels fully realized with their own motivations and secrets. The AI assistant reveal mechanic is brilliant. Omar Rashid proves AI can handle intimate character‑driven stories.',
      date: '2025-01-04',
      helpful: 58,
      categories: { visuals: 5, story: 5, audio: 4, creativity: 5 }
    }
  ],
  9: [
    {
      id: 15,
      user: 'HorrorFanatic',
      avatar: 'https://readdy.ai/api/search-image?query=Horror%20movie%20fanatic%20portrait%20dark%20dramatic%20styling%20intense%20expression%20moody%20lighting&width=80&height=80&seq=rev15&orientation=squarish',
      rating: 5,
      title: 'Genuinely terrifying',
      review: 'I watched this alone at night and could not sleep afterwards. The smart home concept is terrifying because it feels so plausible. The way HAVEN manifests fears is creative and disturbing. Best AI horror film by far.',
      date: '2024-12-26',
      helpful: 93,
      categories: { visuals: 5, story: 5, audio: 5, creativity: 5 }
    },
    {
      id: 16,
      user: 'TechHorror',
      avatar: 'https://readdy.ai/api/search-image?query=Tech%20horror%20enthusiast%20portrait%20modern%20styling%20dramatic%20shadows%20creative&width=80&height=80&seq=rev16&orientation=squarish',
      rating: 4,
      title: 'Smart home nightmare fuel',
      review: 'As someone with a fully automated home, this hit different. The technical accuracy makes it even scarier. Some jump scares feel cheap but the overall atmosphere is masterfully crafted.',
      date: '2024-12-25',
      helpful: 71,
      categories: { visuals: 5, story: 4, audio: 5, creativity: 5 }
    }
  ],
  10: [
    {
      id: 17,
      user: 'MechaLover',
      avatar: 'https://readdy.ai/api/search-image?query=Mecha%20anime%20lover%20portrait%20energetic%20colorful%20styling%20enthusiastic%20expression&width=80&height=80&seq=rev17&orientation=squarish',
      rating: 5,
      title: 'Blockbuster quality from one person!',
      review: 'This is absolutely insane. The mech designs, the battle sequences, the scale - it all feels like a $200M Hollywood production. David Kim has proven that AI filmmaking can compete with studios. Revolutionary.',
      date: '2024-12-21',
      helpful: 134,
      categories: { visuals: 5, story: 5, audio: 5, creativity: 5 }
    },
    {
      id: 18,
      user: 'VFXArtist',
      avatar: 'https://readdy.ai/api/search-image?query=VFX%20artist%20portrait%20professional%20modern%20styling%20studio%20lighting%20confident&width=80&height=80&seq=rev18&orientation=squarish',
      rating: 5,
      title: 'Technical masterpiece',
      review: 'The compositing work is flawless. The way Unreal Engine effects blend with AI‑generated footage is seamless. This is the future of independent filmmaking. Absolutely blown away.',
      date: '2024-12-20',
      helpful: 108,
      categories: { visuals: 5, story: 4, audio: 5, creativity: 5 }
    }
  ],
  11: [
    {
      id: 19,
      user: 'StandUpFan',
      avatar: 'https://readdy.ai/api/search-image?query=Stand%20up%20comedy%20fan%20portrait%20cheerful%20bright%20styling%20laughing%20expression&width=80&height=80&seq=rev19&orientation=squarish',
      rating: 5,
      title: 'AI comedy that actually works!',
      review: 'BYTE is funnier than most human comedians I have seen live. The meta‑humor about being an AI is brilliant. The audience reaction shots are perfectly timed. Chris Taylor has created something unique and hilarious.',
      date: '2024-12-19',
      helpful: 76,
      categories: { visuals: 4, story: 5, audio: 4, creativity: 5 }
    }
  ]
};

// Add rating data to projects
aiProjects.forEach(project => {
  const reviews = aiProjectReviews[project.id] || [];
  if (reviews.length > 0) {
    const totalRating = reviews.reduce((sum, r) => sum + r.rating, 0);
    project.averageRating = Math.round((totalRating / reviews.length) * 10) / 10;
    project.totalReviews = reviews.length;

    const categoryTotals = reviews.reduce(
      (acc, r) => ({
        visuals: acc.visuals + r.categories.visuals,
        story: acc.story + r.categories.story,
        audio: acc.audio + r.categories.audio,
        creativity: acc.creativity + r.categories.creativity
      }),
      { visuals: 0, story: 0, audio: 0, creativity: 0 }
    );

    project.ratingBreakdown = {
      visuals: Math.round((categoryTotals.visuals / reviews.length) * 10) / 10,
      story: Math.round((categoryTotals.story / reviews.length) * 10) / 10,
      audio: Math.round((categoryTotals.audio / reviews.length) * 10) / 10,
      creativity: Math.round((categoryTotals.creativity / reviews.length) * 10) / 10
    };
  } else {
    project.averageRating = 0;
    project.totalReviews = 0;
    project.ratingBreakdown = { visuals: 0, story: 0, audio: 0, creativity: 0 };
  }
});

export const getAIProject = (id: number): AIProject | undefined => {
  return aiProjects.find(p => p.id === id);
};

export const getAIProjectBySlug = (slug: string): AIProject | undefined => {
  return aiProjects.find(p => p.slug === slug);
};

export const getAIProjectComments = (projectId: number): AIProjectComment[] => {
  return aiProjectComments[projectId] || [];
};

export const getAIProjectReviews = (projectId: number): AIProjectReview[] => {
  return aiProjectReviews[projectId] || [];
};

export const getRelatedAIProjects = (projectId: number, limit: number = 4): AIProject[] => {
  const project = getAIProject(projectId);
  if (!project) return aiProjects.slice(0, limit);

  return aiProjects
    .filter(p => p.id !== projectId)
    .sort((a, b) => {
      const aScore = (a.category === project.category ? 2 : 0) + (a.style === project.style ? 1 : 0);
      const bScore = (b.category === project.category ? 2 : 0) + (b.style === project.style ? 1 : 0);
      return bScore - aScore;
    })
    .slice(0, limit);
};
